import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  Heart,
  Share2,
  MapPin,
  Star,
  Clock,
  Navigation,
  Phone,
  Globe,
  Info,
  Lightbulb,
  ThumbsUp,
  MapPinned,
  Camera,
  Calendar,
  Users,
  Bookmark,
  ExternalLink
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { placesApi, favoritesApi, tripsApi } from '../services/api';

const PlaceDetailPage = ({ place, onBack, onNavigateToPlace }) => {
  const { darkMode } = useTheme();
  const { isAuthenticated, user, updateFavorites } = useAuth();
  const [activeTab, setActiveTab] = useState('about');
  const [isLiked, setIsLiked] = useState(false);
  const [similarPlaces, setSimilarPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiTips, setAiTips] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  // Place details with fallbacks - prefer 'about' field for detailed blog-style descriptions
  const placeData = {
    id: place?.id || '',
    name: place?.name || 'Mekan Adı',
    description: place?.about || place?.description || 'Bu mekan hakkında detaylı bilgi bulunmaktadır.',
    image: place?.image || place?.image_url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800',
    images: place?.images || [place?.image || place?.image_url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800'],
    category: place?.category_name || place?.category || 'Genel',
    rating: place?.rating || 4.5,
    reviewCount: place?.review_count || 128,
    distance: place?.distance || '2.5 km',
    duration: place?.visit_duration || '1-2 saat',
    address: place?.address || place?.location || 'Giresun Merkez',
    phone: place?.phone || null,
    website: place?.website || null,
    openHours: place?.open_hours || '09:00 - 18:00',
    bestTime: place?.best_time || 'Sabah veya gün batımı',
    entranceFee: place?.entrance_fee || 'Ücretsiz',
    facilities: place?.facilities || ['Otopark', 'Tuvalet', 'Kafe'],
    tags: place?.tags || ['tarihi', 'manzara', 'fotoğraf'],
    tips: place?.tips || null,
    latitude: place?.latitude || place?.coordinates?.lat || null,
    longitude: place?.longitude || place?.coordinates?.lng || null,
    maps_url: place?.maps_url || null,
  };

  // Open Google Maps with directions
  const openGoogleMapsDirections = () => {
    const { maps_url, latitude, longitude, name, address } = placeData;
    
    // Priority 1: Use direct Google Maps link if available
    if (maps_url) {
      window.open(maps_url, '_blank');
      return;
    }
    
    // Priority 2: Use coordinates
    if (latitude && longitude) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(url, '_blank');
      return;
    }
    
    // Priority 3: Use address
    if (address) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address + ', Giresun, Turkey')}`;
      window.open(url, '_blank');
      return;
    }
    
    // Last resort: search by name
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + ', Giresun, Turkey')}`;
    window.open(url, '_blank');
  };

  // Static tips and recommendations based on category
  const getTipsForCategory = (category) => {
    const tips = {
      'Tarihi': {
        tips: [
          '🕐 Sabah erken saatlerde ziyaret edin, kalabalık olmaz',
          '📸 Fotoğraf çekmek için en iyi saat gün batımıdır',
          '👟 Rahat ayakkabı giyin, yürüyüş mesafesi var',
          '🎫 Hafta içi ziyaret edin, bilet kuyrukları kısa olur',
          '📱 Sesli rehber uygulaması indirin, daha fazla bilgi alın'
        ],
        recommendations: [
          'Yanınıza su ve atıştırmalık alın',
          'Rehberli tur tercih edin, tarihi detayları kaçırmayın',
          'Fotoğraf makinenizi unutmayın',
          'Mekanın hikayesini önceden araştırın'
        ]
      },
      'Doğa': {
        tips: [
          '🌅 Gün doğumu veya gün batımı manzarası muhteşem',
          '🥾 Trekking ayakkabısı şart',
          '🧴 Güneş kremi ve böcek kovucu alın',
          '🎒 Sırt çantasında yeterli su bulundurun',
          '🗺️ Offline harita indirin, sinyal zayıf olabilir'
        ],
        recommendations: [
          'Hava durumunu kontrol edin',
          'Yanınızda ilk yardım çantası bulundurun',
          'Doğayı koruyun, çöp bırakmayın',
          'Fotoğraf için tripod kullanın'
        ]
      },
      'Yeme-İçme': {
        tips: [
          '🍽️ Öğle saatlerinde gidin, taze yemekler hazır olur',
          '💳 Nakit bulundurun, bazı yerler kart almaz',
          '📞 Hafta sonu için rezervasyon yapın',
          '🥗 Yöresel lezzetleri mutlaka deneyin',
          '👨‍🍳 Şefin önerilerini sorun'
        ],
        recommendations: [
          'Yerel halkın gittiği yerleri tercih edin',
          'Mevsim ürünlerini deneyin',
          'Porsiyon boyutlarını sorun',
          'Tatlı için yer bırakın'
        ]
      },
      'Fotoğraf': {
        tips: [
          '📷 Altın saat (gün doğumu/batımı) en iyi ışık',
          '🔋 Yedek batarya ve hafıza kartı alın',
          '📐 Farklı açılardan çekim yapın',
          '👥 Kalabalık olmayan saatleri tercih edin',
          '🌤️ Bulutlu havalar yumuşak ışık sağlar'
        ],
        recommendations: [
          'Tripod kullanın stabil çekimler için',
          'Geniş açı lens tercih edin',
          'Ham (RAW) formatta çekin',
          'Yerel insanlardan izin alın'
        ]
      }
    };
    return tips[category] || tips['Tarihi'];
  };

  const categoryData = getTipsForCategory(placeData.category);

  useEffect(() => {
    // Check if place is liked
    if (user?.favorites?.includes(place?.id)) {
      setIsLiked(true);
    }

    // Fetch similar places
    const fetchSimilarPlaces = async () => {
      try {
        const allPlaces = await placesApi.getFeatured();
        const similar = allPlaces
          .filter(p => p.id !== place?.id && p.category_name === placeData.category)
          .slice(0, 4);
        setSimilarPlaces(similar);
      } catch (error) {
        console.error('Error fetching similar places:', error);
      }
    };

    fetchSimilarPlaces();
  }, [place, user]);

  const handleToggleLike = async () => {
    if (!isAuthenticated) return;

    try {
      const result = await favoritesApi.toggle(place.id);
      setIsLiked(result.is_favorite);
      if (updateFavorites) {
        const newFavorites = result.is_favorite 
          ? [...(user?.favorites || []), place.id]
          : (user?.favorites || []).filter(id => id !== place.id);
        updateFavorites(newFavorites);
      }
    } catch (error) {
      console.error('Toggle favorite error:', error);
    }
  };

  const handleGetAiTips = async () => {
    setAiLoading(true);
    try {
      const response = await tripsApi.mivChat(
        `${placeData.name} hakkında kısa ipuçları ve öneriler ver. En iyi ziyaret zamanı, ne yapılmalı, nelere dikkat edilmeli?`
      );
      if (response.success) {
        setAiTips(response.message);
      }
    } catch (error) {
      console.error('AI tips error:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const tabs = [
    { id: 'about', label: 'Hakkında', icon: Info },
    { id: 'tips', label: 'İpuçları', icon: Lightbulb },
    { id: 'recommendations', label: 'Öneriler', icon: ThumbsUp },
    { id: 'similar', label: 'Benzer Yerler', icon: MapPinned },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <div className="space-y-6">
            {/* Description */}
            <div>
              <h3 className={`font-semibold ${textPrimary} mb-2`}>Açıklama</h3>
              <p className={`${textSecondary} leading-relaxed`}>{placeData.description}</p>
            </div>

            {/* Quick Info */}
            <div className={`${bgCard} rounded-xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Genel Bilgiler</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className={`w-5 h-5 ${textSecondary}`} />
                  <div>
                    <p className={`text-xs ${textSecondary}`}>Ziyaret Süresi</p>
                    <p className={`text-sm font-medium ${textPrimary}`}>{placeData.duration}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${textSecondary}`} />
                  <div>
                    <p className={`text-xs ${textSecondary}`}>Çalışma Saatleri</p>
                    <p className={`text-sm font-medium ${textPrimary}`}>{placeData.openHours}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Star className={`w-5 h-5 text-yellow-500`} />
                  <div>
                    <p className={`text-xs ${textSecondary}`}>En İyi Zaman</p>
                    <p className={`text-sm font-medium ${textPrimary}`}>{placeData.bestTime}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Bookmark className={`w-5 h-5 ${textSecondary}`} />
                  <div>
                    <p className={`text-xs ${textSecondary}`}>Giriş Ücreti</p>
                    <p className={`text-sm font-medium ${textPrimary}`}>{placeData.entranceFee}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Facilities */}
            <div>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Olanaklar</h3>
              <div className="flex flex-wrap gap-2">
                {placeData.facilities.map((facility, index) => (
                  <span
                    key={index}
                    className={`px-3 py-1.5 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full text-sm ${textPrimary}`}
                  >
                    {facility}
                  </span>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className={`${bgCard} rounded-xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>İletişim</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#1e3a5f]" />
                  <span className={textSecondary}>{placeData.address}</span>
                </div>
                {placeData.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#1e3a5f]" />
                    <span className={textSecondary}>{placeData.phone}</span>
                  </div>
                )}
                {placeData.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#1e3a5f]" />
                    <a href={placeData.website} className="text-[#1e3a5f] hover:underline">
                      Web sitesini ziyaret et
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'tips':
        // Parse tips from database - can be multiline or comma separated
        const parseDatabaseTips = (tipsString) => {
          if (!tipsString) return [];
          // Split by newline or bullet points
          return tipsString
            .split(/[\n•\-]/)
            .map(tip => tip.trim())
            .filter(tip => tip.length > 0);
        };
        
        const databaseTips = parseDatabaseTips(placeData.tips);
        const hasDatabaseTips = databaseTips.length > 0;
        
        return (
          <div className="space-y-6">
            {/* Main Tips Section */}
            <div>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Ziyaret İpuçları</h3>
              <div className="space-y-3">
                {/* Database tips first (from admin panel) */}
                {hasDatabaseTips ? (
                  databaseTips.map((tip, index) => (
                    <div
                      key={`db-${index}`}
                      className={`${bgCard} rounded-xl p-4 flex items-start gap-3`}
                    >
                      <div className="w-8 h-8 bg-[#0d9488]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-4 h-4 text-[#0d9488]" />
                      </div>
                      <p className={`${textPrimary} text-sm`}>{tip}</p>
                    </div>
                  ))
                ) : (
                  /* Fallback to static tips if no database tips */
                  categoryData.tips.map((tip, index) => (
                    <div
                      key={`static-${index}`}
                      className={`${bgCard} rounded-xl p-4 flex items-start gap-3`}
                    >
                      <div className="w-8 h-8 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Lightbulb className="w-4 h-4 text-[#1e3a5f]" />
                      </div>
                      <p className={`${textPrimary} text-sm`}>{tip}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* AI Tips */}
            <div className={`${bgCard} rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-semibold ${textPrimary}`}>🤖 AI İpuçları</h3>
                {!aiTips && (
                  <button
                    onClick={handleGetAiTips}
                    disabled={aiLoading}
                    className="px-3 py-1.5 bg-[#1e3a5f] text-white text-sm rounded-lg hover:bg-[#2a4a73] transition-colors disabled:opacity-50"
                  >
                    {aiLoading ? 'Yükleniyor...' : 'AI\'dan Sor'}
                  </button>
                )}
              </div>
              {aiTips ? (
                <p className={`${textSecondary} text-sm whitespace-pre-wrap`}>{aiTips}</p>
              ) : (
                <p className={`${textSecondary} text-sm`}>
                  Miv'den bu mekan hakkında kişiselleştirilmiş ipuçları alın.
                </p>
              )}
            </div>
          </div>
        );

      case 'recommendations':
        return (
          <div className="space-y-6">
            {/* What to do */}
            <div>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Ne Yapmalı?</h3>
              <div className="space-y-2">
                {categoryData.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`${bgCard} rounded-xl p-4 flex items-center gap-3`}
                  >
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{index + 1}</span>
                    </div>
                    <p className={`${textPrimary} text-sm`}>{rec}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Etiketler</h3>
              <div className="flex flex-wrap gap-2">
                {placeData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Best For */}
            <div className={`${bgCard} rounded-xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-3`}>Kimler İçin İdeal?</h3>
              <div className="grid grid-cols-2 gap-3">
                {['Aileler', 'Çiftler', 'Fotoğrafçılar', 'Doğa Severler'].map((group, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Users className={`w-4 h-4 ${textSecondary}`} />
                    <span className={`text-sm ${textPrimary}`}>{group}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'similar':
        return (
          <div className="space-y-4">
            <h3 className={`font-semibold ${textPrimary} mb-3`}>Benzer Yerler</h3>
            {similarPlaces.length > 0 ? (
              <div className="space-y-3">
                {similarPlaces.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => onNavigateToPlace && onNavigateToPlace(p)}
                    className={`${bgCard} rounded-xl overflow-hidden flex cursor-pointer hover:shadow-md transition-shadow`}
                  >
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-24 h-24 object-cover"
                    />
                    <div className="p-3 flex-1">
                      <h4 className={`font-semibold ${textPrimary} mb-1`}>{p.name}</h4>
                      <p className={`text-xs ${textSecondary} line-clamp-2 mb-2`}>{p.description}</p>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className={`text-sm ${textPrimary}`}>{p.rating}</span>
                        <span className={`text-sm ${textSecondary}`}>• {p.distance}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={textSecondary}>Benzer mekan bulunamadı.</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      {/* Hero Image */}
      <div className="relative h-80">
        <img
          src={placeData.image}
          alt={placeData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Top Bar - Back Button & Action Buttons */}
        <div className="absolute top-4 left-0 right-0">
          <div className="max-w-3xl mx-auto px-4 flex items-center justify-between">
            <button
              onClick={onBack}
              className={`w-10 h-10 ${darkMode ? 'bg-slate-800/90' : 'bg-white/90'} rounded-full flex items-center justify-center shadow-lg`}
            >
              <ChevronLeft className={`w-6 h-6 ${textPrimary}`} />
            </button>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleToggleLike}
                className={`w-10 h-10 ${darkMode ? 'bg-slate-800/90' : 'bg-white/90'} rounded-full flex items-center justify-center shadow-lg`}
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : textSecondary}`} />
              </button>
              <button
                className={`w-10 h-10 ${darkMode ? 'bg-slate-800/90' : 'bg-white/90'} rounded-full flex items-center justify-center shadow-lg`}
              >
                <Share2 className={`w-5 h-5 ${textSecondary}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Place Info Overlay */}
        <div className="absolute bottom-10 left-0 right-0 text-white">
          <div className="max-w-3xl mx-auto px-4">
            <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium mb-2 inline-block">
              {placeData.category}
            </span>
            <h1 className="text-2xl font-bold mb-1">{placeData.name}</h1>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span>{placeData.rating}</span>
                <span className="text-white/70">({placeData.reviewCount} yorum)</span>
              </div>
              <div className="flex items-center gap-1">
                <Navigation className="w-4 h-4" />
                <span>{placeData.distance}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 -mt-4 relative z-10">
        {/* Tabs */}
        <div className={`${bgCard} rounded-xl p-1 flex gap-1 mb-6 shadow-lg`}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#1e3a5f] text-white'
                    : `${textSecondary} hover:bg-gray-100 dark:hover:bg-slate-700`
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {renderTabContent()}
        </div>

        {/* Action Buttons - Static position to not block bottom nav */}
        <div className="pb-24 px-4">
          <div className="flex gap-3">
            <button 
              onClick={openGoogleMapsDirections}
              className="flex-1 py-3 bg-[#1e3a5f] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#2a4a73] transition-colors active:scale-95"
            >
              <Navigation className="w-5 h-5" />
              Yol Tarifi Al
            </button>
            <button 
              onClick={() => placeData.phone && window.open(`tel:${placeData.phone}`, '_self')}
              className={`px-4 py-3 ${bgCard} border ${borderColor} rounded-xl ${placeData.phone ? 'hover:bg-gray-100 dark:hover:bg-slate-700' : 'opacity-50 cursor-not-allowed'}`}
              disabled={!placeData.phone}
            >
              <Phone className={`w-5 h-5 ${textSecondary}`} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceDetailPage;
